
import { useContext, useEffect, useMemo, useState, memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Divider,
    Paper,
    Typography,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import Iconify from 'src/components/iconify';
import { useCurrentWorkspace } from 'src/context/reducers/app-settings';
import { 
    setFilterLoadedCountry, 
    setFilterLoadedDevice, 
    setFilterLoadedKeyword, 
    setFilterLoadedPlatform, 
    setFilterMinDatePicker, 
    setFilterDomainPLA, 
    setFilterDate, 
    setFilterCompare, 
    setFilterDateCompare 
} from 'src/context/reducers/filter-settings/filterSlice';
import { 
    useGetFiltersQuery, 
} from 'src/context/api/competitorsMonitoring/api';
import countriesJson from 'src/utils/countries.json';
import { subDays } from 'date-fns';
import AdsLoadingScreen from 'src/components/competitive-intelligence/components/AdsLoadingScreen';
import { CountryFilter } from 'src/components/competitive-intelligence/components/CountryFilter';
import { useCountryData } from 'src/components/competitive-intelligence/hooks/useCountryData';
import { useAdsData } from 'src/components/competitive-intelligence/hooks/useAdsData';
import { DomainItem, PlaItem, TextAdItem } from 'src/components/competitive-intelligence/types';
import { CompetitiveIntelligenceContext } from 'src/components/competitive-intelligence/components/CompetitiveIntelligenceProvider';
import AdsFiltersDrawer from 'src/components/competitive-intelligence/components/AdsFiltersDrawer';
import NewLeftDomains from 'src/components/competitive-intelligence/components/LeftDomains';
import NewPlaCarousel from 'src/components/competitive-intelligence/components/PlaCarousel';
import NewTextAdsList from 'src/components/competitive-intelligence/components/TextAdsList';
import NewRightPanelAds from 'src/components/competitive-intelligence/components/RightPanelAds';
import { filterPlaItems, filterTextAds } from 'src/components/competitive-intelligence/utils/filters';
import { sortPlaItems, sortTextAds } from 'src/components/competitive-intelligence/utils/sort';
import { searchPla, searchTextAds, searchRightPanel } from 'src/components/competitive-intelligence/utils/search';
import TopAttackedSearchTerms from 'src/components/competitive-intelligence/components/TopAttackedSearchTerms';
import { normalizeDomain } from 'src/components/competitive-intelligence/utils/domain';


// Shared module-level constants (stable references)
// These avoid recreating empty arrays in render and keep dependencies simple
const EMPTY_STRING_ARRAY = [] as string[];
const EMPTY_NUM_RANGE = [] as number[];

// Generic data pipeline: domain filter → store filters → search → sort
// Keeps transformation steps consistent and easy to follow for each dataset
function runPipeline<T>(args: {
    items: T[];
    domain?: string | null;
    filterFn: (items: T[]) => T[];
    searchFn: (items: T[]) => T[];
    sortFn: (items: T[]) => T[];
}): T[] {
    const { items, domain, filterFn, searchFn, sortFn } = args;
    const byDomain = domain ? (items as any[]).filter((it) => (it as any).domain === domain) as T[] : items;
    const filtered = filterFn(byDomain);
    const searched = searchFn(filtered);
    return sortFn(searched);
}

/*
  Ads page logical flow (high-level):
  1) Read workspace/context (country), Redux filters, and fetch countries + ads data.
  2) Normalize filters and memoize derived values (e.g., brand mode, flagged domains).
  3) Build domain list for the left panel and keep selected domain in sync with Drawer.
  4) For each dataset (PLA, Text, RightPanel), run the same pipeline: domain → filters → search → sort.
  5) Compute visibility flags to decide layout (PLA carousel, Text list, Right panel grid).
  6) Render: country selector, left domains, content (PLA/Text/Right) or empty state; show skeleton while loading.
*/

// Memoized child components to avoid unnecessary re-renders
const MemoLeftDomains = memo(NewLeftDomains, (prevProps, nextProps) =>
    prevProps.items === nextProps.items &&
    prevProps.selected === nextProps.selected &&
    prevProps.adType === nextProps.adType);


const MemoPlaCarousel = memo(NewPlaCarousel, (prevProps, nextProps) =>
    prevProps.items === nextProps.items &&
    prevProps.layout === nextProps.layout &&
    prevProps.showBrandChip === nextProps.showBrandChip
);

const MemoTextAdsList = memo(NewTextAdsList, (prevProps, nextProps) =>
    prevProps.items === nextProps.items &&
    prevProps.pageSize === nextProps.pageSize &&
    prevProps.showBrandChip === nextProps.showBrandChip
);

const MemoRightPanelAds = memo(NewRightPanelAds, (prevProps, nextProps) =>
    prevProps.items === nextProps.items &&
    prevProps.columns === nextProps.columns &&
    prevProps.pageSize === nextProps.pageSize &&
    prevProps.showBrandChip === nextProps.showBrandChip &&
    prevProps.totalCount === nextProps.totalCount
);

export default function Ads() {
    const { t } = useTranslation();
    const [selectedDomain, setSelectedDomain] = useState<string>('');
    const ciContext = useContext(CompetitiveIntelligenceContext);
    const dispatch = useDispatch();
    const { adType, searchQuery, sortBy, sortDirection } = useSelector((state: any) => state.competitiveIntelligenceAdsUi || { adType: 'ALL', searchQuery: '', sortBy: 'Freq.', sortDirection: 'desc' });
    const workspace: any = useCurrentWorkspace();
    const filterStore = useSelector((state: any) => state.filters);

    // Use selectedCountry from context instead of local state to avoid infinite loops
    const selectedCountry = ciContext?.selectedCountry || null;

    // Fetch countries data
    const { countriesData, isLoading: isLoadingCountries } = useCountryData();
    const location = useLocation();

    // Fetch ads data based on effective selected country
    const { plaItems, textAds, domainItems, rightPanelItems, isLoading: isLoadingAds } = useAdsData(selectedCountry);

    // Local Filters Drawer (simplified version adapted from GlobalPlaAds)
    const [filtersDrawerOpen, setFiltersDrawerOpen] = useState(false);

    const routePathname = location.pathname;

    // Deep link support on Ads: ?country=FR → map to country name and persist
    useEffect(() => {
        const params = new URLSearchParams(location.search || (typeof window !== 'undefined' ? window.location.search : ''));
        const codeParam = params.get('country');
        if (!codeParam) return;
        const mappedName = countriesJson?.find((c: any) => String(c.code).toUpperCase() === String(codeParam).toUpperCase())?.name;
        if (mappedName && countriesData.some((c) => c.country_name === mappedName)) {
            try {
                ciContext?.setSelectedCountry?.(mappedName);
                if (workspace?.id) localStorage.setItem(`ci.selectedCountry.${workspace.id}`, mappedName);
            } catch { console.log("unable to get ci.selectedCountry from localStorage") }
        }
    }, [location.search, countriesData, ciContext, workspace?.id]);


    // Listen to global header filter button to open the page-specific filters (Ads page)
    useEffect(() => {
        const handler = () => {
            setFiltersDrawerOpen(true);
        };
        window.addEventListener('openPageFiltersDrawer', handler as any);
        return () => window.removeEventListener('openPageFiltersDrawer', handler as any);
    }, []);

    // Close the local filters drawer when navigating to another route (avoid leaking state from other pages)
    useEffect(() => {
        setFiltersDrawerOpen(false);
    }, [routePathname]);

    // Derive filter values from Redux store (stable defaults)
    const domainsPLA = useMemo(() => ((filterStore as any)?.domainsPLA ?? EMPTY_STRING_ARRAY) as string[], [filterStore]);
    const advertizersPLA = useMemo(() => ((filterStore as any)?.advertizersPLA ?? EMPTY_STRING_ARRAY) as string[], [filterStore]);
    const impressionSharePLA = useMemo(() => ((filterStore as any)?.impressionSharePLA ?? EMPTY_NUM_RANGE) as number[],  [filterStore]);
    const averagePositionPLA = useMemo(() => ((filterStore as any)?.averagePositionPLA ?? EMPTY_NUM_RANGE) as number[], [filterStore]);
    const pricingRangePLA = useMemo(() => ((filterStore as any)?.pricingRangePLA ?? EMPTY_NUM_RANGE) as number[], [filterStore]);

    const applyStoreFiltersToPla = useCallback((items: PlaItem[]): PlaItem[] => {
        return filterPlaItems(items, {
            domainsPLA,
            brandsPLA: (filterStore as any)?.brandsPLA,
            advertizersPLA,
            impressionSharePLA,
            averagePositionPLA,
            pricingRangePLA,
        });
    }, [domainsPLA, advertizersPLA, impressionSharePLA, averagePositionPLA, pricingRangePLA, filterStore]);

    const applyStoreFiltersToText = useCallback((items: TextAdItem[]): TextAdItem[] => {
        return filterTextAds(items, {
            domainsPLA,
            brandsPLA: (filterStore as any)?.brandsPLA,
            advertizersPLA,
            impressionSharePLA,
            averagePositionPLA,
        });
    }, [domainsPLA, advertizersPLA, impressionSharePLA, averagePositionPLA, filterStore]);

    const applyStoreFiltersToRightPanel = useCallback((items: PlaItem[]): PlaItem[] => applyStoreFiltersToPla(items), [applyStoreFiltersToPla]);

    // Run the dataset pipeline: domain → filters → search → sort
    const pipedPlaItems = useMemo(() => runPipeline({
        items: plaItems,
        domain: selectedDomain,
        filterFn: (items) => applyStoreFiltersToPla(items),
        searchFn: (items) => searchPla(items as any, searchQuery) as any,
        sortFn: (items) => sortPlaItems(items as any, sortBy, sortDirection) as any,
    }), [plaItems, selectedDomain, applyStoreFiltersToPla, searchQuery, sortBy, sortDirection]);

    const pipedTextAds = useMemo(() => runPipeline({
        items: textAds,
        domain: selectedDomain,
        filterFn: (items) => applyStoreFiltersToText(items),
        searchFn: (items) => searchTextAds(items as any, searchQuery) as any,
        sortFn: (items) => sortTextAds(items as any, sortBy, sortDirection) as any,
    }), [textAds, selectedDomain, applyStoreFiltersToText, searchQuery, sortBy, sortDirection]);

    const pipedRightPanelItems = useMemo(() => runPipeline({
        items: rightPanelItems,
        domain: selectedDomain,
        filterFn: (items) => applyStoreFiltersToRightPanel(items),
        searchFn: (items) => searchRightPanel(items as any, searchQuery) as any,
        sortFn: (items) => sortPlaItems(items as any, sortBy, sortDirection) as any,
    }), [rightPanelItems, selectedDomain, applyStoreFiltersToRightPanel, searchQuery, sortBy, sortDirection]);


    type Ad = {
        domain: string;
        [key: string]: unknown;
    };

    // Mémoriser le statut du filtre brand pour éviter les recalculs
    const brandFilterOn = useMemo(() => 
        Array.isArray(filterStore?.brandsPLA) && filterStore.brandsPLA.includes('__BRAND_INFRINGEMENT__'),
        [filterStore?.brandsPLA]
    );

    // Mémoriser les domaines flaggés pour éviter les recalculs coûteux
    const flaggedDomains = useMemo(() => {
        if (!brandFilterOn) return new Set<string>();
        
        const sources: Record<string, Ad[]> = {
            ALL: [...plaItems, ...textAds, ...rightPanelItems],
            PLA: [...plaItems, ...rightPanelItems],
            TEXT: textAds,
            RightPanel: rightPanelItems,
        } as any;
        const allAds = (sources as any)[adType] || sources.ALL;
        
        return new Set(
            allAds
                .filter((ad: any) => ad.brandInfringementTitle || ad.brandInfringementDescription)
                .map((ad: any) => ad.domain)
                .filter(Boolean)
        );
    }, [brandFilterOn, adType, plaItems, textAds, rightPanelItems]);

    const filteredDomainItems = useMemo((): DomainItem[] => {
        if (!brandFilterOn) return domainItems;
        return domainItems.filter((d) => flaggedDomains.has(d.domain));
    }, [domainItems, brandFilterOn, flaggedDomains]);

    // Apply Drawer domains filter to the left panel list
    const leftPanelDomainItems = useMemo(() => {
        if (!domainsPLA || domainsPLA.length === 0) return filteredDomainItems;
        const setSel = new Set(domainsPLA);
        return filteredDomainItems.filter((d) => setSel.has(d.domain));
    }, [filteredDomainItems, domainsPLA]);

    // Mémoriser les domaines disponibles pour éviter les re-renders
    const availableDomains = useMemo(() => 
        leftPanelDomainItems.map(d => d.domain), 
        [leftPanelDomainItems]
    );

    // Prefer selecting client's domain if available, otherwise the first domain
    const friendlyDomainsSet = useMemo(() => {
        const friendly = Array.from(new Set([
            ...(Array.isArray(workspace?.friendlyDomains) ? (workspace.friendlyDomains as string[]).filter(Boolean) : []),
            ...(workspace?.url ? [workspace.url] : []),
        ]
            .map((d: string) => normalizeDomain(d))
            .filter(Boolean)));
        return new Set(friendly);
    }, [workspace?.friendlyDomains, workspace?.url]);

    const preferredDomain = useMemo(() => {
        const firstFriendly = leftPanelDomainItems.find((d) => friendlyDomainsSet.has(normalizeDomain(d.domain)));
        return (firstFriendly?.domain || leftPanelDomainItems[0]?.domain || '').toString();
    }, [leftPanelDomainItems, friendlyDomainsSet]);

    // Keep selected domain in sync with available list and Drawer filter
    useEffect(() => {
        const hasAvailable = availableDomains.length > 0;
        if (!hasAvailable) {
            if (selectedDomain) setSelectedDomain('');
            return;
        }

        // Prefer client's domain if present, else keep current if valid, else first available
        let desired: string | null = null;
        if (selectedDomain && availableDomains.includes(selectedDomain)) {
            desired = selectedDomain;
        } else if (preferredDomain && availableDomains.includes(preferredDomain)) {
            desired = preferredDomain;
        } else {
            desired = availableDomains[0];
        }

        if (desired && desired !== selectedDomain) {
            setSelectedDomain(desired);
        }

        // Sync Drawer domain filter with the effective selection
        const currentFilterDomains: string[] = (filterStore as any)?.domainsPLA || [];
        if (desired && (currentFilterDomains.length !== 1 || currentFilterDomains[0] !== desired)) {
            dispatch(setFilterDomainPLA([desired]));
        }
    }, [availableDomains, preferredDomain, selectedDomain, filterStore, dispatch]);


     const { data: filtersData } = useGetFiltersQuery({
        workspaceId: workspace?.id,
        start_date: filterStore?.start,
        end_date: filterStore?.end,
        countries: filterStore?.countries,
        devices: filterStore?.devices,
        platforms: filterStore?.platforms,
    }, { skip: !workspace?.id });


    useEffect(() => {
        if (filtersData) {
            dispatch(setFilterLoadedCountry(filtersData.countries || []));
            dispatch(setFilterLoadedDevice(filtersData.devices || []));
            dispatch(setFilterLoadedPlatform(filtersData.platforms || []));
            dispatch(setFilterLoadedKeyword(filtersData.keywords));

            if (filtersData.min_date) {
                const d = new Date(filtersData.min_date);
                d.setFullYear(2023);
                dispatch(setFilterMinDatePicker(d.toUTCString()));
            }
        }
    }, [filtersData, dispatch]);

    // Apply adType filter to visible datasets, layout adapts based on these
    // Mémoriser les conditions de filtrage pour éviter les recalculs
    const shouldShowPla = useMemo(() => adType === 'ALL' || adType === 'PLA', [adType]);
    const shouldShowText = useMemo(() => adType === 'ALL' || adType === 'TEXT', [adType]);
    const shouldShowRight = useMemo(() => adType === 'ALL' || adType === 'RightPanel' || adType === 'PLA', [adType]);

    const visiblePlaItems = useMemo(
        () => shouldShowPla ? pipedPlaItems : [],
        [pipedPlaItems, shouldShowPla]
    );
    const visibleTextAds = useMemo(
        () => shouldShowText ? pipedTextAds : [],
        [pipedTextAds, shouldShowText]
    );
    const visibleRightPanelItems = useMemo(
        () => shouldShowRight ? pipedRightPanelItems : [],
        [pipedRightPanelItems, shouldShowRight]
    );

    // Presence-based layout flags (computed from visible datasets) - mémorisés
    const hasPla = useMemo(() => visiblePlaItems.length > 0, [visiblePlaItems.length]);
    const hasText = useMemo(() => visibleTextAds.length > 0, [visibleTextAds.length]);
    const hasRight = useMemo(() => visibleRightPanelItems.length > 0, [visibleRightPanelItems.length]);
    const onlyPla = useMemo(() => hasPla && !hasText && !hasRight, [hasPla, hasText, hasRight]);
    
    // Check if all visible datasets are empty
    const hasNoResults = useMemo(() => !hasPla && !hasText && !hasRight, [hasPla, hasText, hasRight]);
    // Single boolean to drive loading state for the view
    const isLoadingView = useMemo(() => 
        isLoadingCountries ||
        isLoadingAds ||
        (!selectedCountry && countriesData.length > 0) ||
        (!selectedDomain && leftPanelDomainItems.length > 0),
        [isLoadingCountries, isLoadingAds, selectedCountry, countriesData.length, selectedDomain, leftPanelDomainItems.length]
    );

    // Drawer requirements
    // Build advertiser counts based on available items (fallback on domain as seller identifier)

    // Callbacks mémorisés pour éviter les re-renders
    const handleDomainSelect = useCallback((d: string) => {
        setSelectedDomain(d);
        // Keep Drawer filter in sync with left selection
        const domains: string[] = (filterStore as any)?.domainsPLA || [];
        if (domains.length !== 1 || domains[0] !== d) {
            dispatch(setFilterDomainPLA([d]));
        }
    }, [filterStore, dispatch]);

    const handleCountryChange = useCallback((c: string | null) => {
        if (c) {
            try { ciContext?.setSelectedCountry?.(c); } catch { console.log("Error selecting country") }
        }
    }, [ciContext]);

    // Handlers for Drawer controls

    

    useEffect(() => {
        dispatch(setFilterDate({
            start: new Date(new Date().setDate(new Date().getDate() - 30)).toISOString()
        }))

        dispatch(setFilterCompare(false))

        dispatch(setFilterDateCompare({
            compareStart: subDays(new Date(), 60).toISOString(),
            compareEnd: subDays(new Date(), 31).toISOString(),
        }))
    }, [dispatch])

    return (
        <Box sx={{ overflowX: 'hidden', width: '100%' }}>
            <Box sx={{ mt: 2 }}>
                <CountryFilter
                    countries={countriesData}
                    selectedCountry={selectedCountry}
                            onCountryChange={handleCountryChange}
                    backgroundColor="transparent"
                />
            </Box>
            <Divider sx={{ mb: 0, mt: "8px" }} />
            <Paper sx={{ overflow: 'hidden', mt: 0, backgroundColor: 'transparent', position: 'relative' }}>
                {/* Main layout: three columns — left domain list | vertical divider | main content */}
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: '280px 1px 1fr',
                        gap: 2,
                        height: 'calc(100vh - 265px)',
                        opacity: 1,
                        transform: 'translateY(0)',
                        transition: 'opacity 0.6s ease-in-out, transform 0.6s ease-in-out',
                    }}
                >
                    <Box sx={{ gridColumn: '1', height: '100%', display: 'flex', flexDirection: 'column' }}>
                        <MemoLeftDomains
                            items={filteredDomainItems}
                            selected={selectedDomain}
                            onSelect={handleDomainSelect}
                            adType={adType}
                        />
                    </Box>

                    {/* Vertical separator that stretches to touch the horizontal divider above */}
                    <Box sx={{ gridColumn: '2', bgcolor: 'divider', width: '1px', height: '100%' }} />

                    <Box sx={{ gridColumn: '3', minWidth: 0, height: '100%', overflowY: 'auto', overflowX: 'hidden', position: 'relative', pr: 1 }}>
                        {isLoadingView ? (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    transition: 'opacity 0.5s ease-in-out, transform 0.5s ease-in-out',
                                    zIndex: 1,
                                }}
                            >
                                <AdsLoadingScreen externalLoadingState={isLoadingView} />
                            </Box>
                        ) : (
                            <>
                                {/* Show "0 results found" when no data is available */}
                                {hasNoResults ? (
                                    <Box sx={{ pt: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
                                        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', backgroundColor: 'background.neutral', maxWidth: '400px' }}>
                                            <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                                                0 results found
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {t("dashboard.global.competitiveIntelligence.no_results_found_description")}
                                            </Typography>
                                        </Paper>
                                    </Box>
                                ) : (
                                    <>
                                        {/* Top attacked search terms (compact/expanded) */}
                                        {/* <Box sx={{ pt: 2 }}>
                                            <TopAttackedSearchTerms />
                                        </Box> */}
                                        {/* Top row: PLA section. Hidden when empty. If PLA is the only content on the page, switch to a responsive grid layout. */}
                                        {hasPla && (
                                            <Box sx={{ pt: 2 }}>
                                                <MemoPlaCarousel items={visiblePlaItems} layout={onlyPla ? 'grid' : 'carousel'} showBrandChip={brandFilterOn} />
                                            </Box>
                                        )}

                                        {/* Bottom row: Text / Right Panel depending on availability */}
                                        {hasText && hasRight && (
                                            /* Both present → two columns: Text (fluid) | Right panel (fixed 360px) */
                                            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 360px', pt: 2, gap: 2 }}>
                                                <Box>
                                                    <MemoTextAdsList items={visibleTextAds} showBrandChip={brandFilterOn} />
                                                </Box>
                                                <Box>
                                                    <MemoRightPanelAds 
                                                        items={visibleRightPanelItems} 
                                                        showBrandChip={brandFilterOn} 
                                                        totalCount={pipedRightPanelItems.length}
                                                    />
                                                </Box>
                                            </Box>
                                        )}

                                        {/* Only Text present → full width */}
                                        {!hasRight && hasText && (
                                            <Box sx={{ pt: 2 }}>
                                                <MemoTextAdsList items={visibleTextAds} showBrandChip={brandFilterOn} />
                                            </Box>
                                        )}

                                        {/* Only Right panel present → full width, four cards per row */}
                                        {!hasText && hasRight && (
                                            <Box sx={{ pt: 2 }}>
                                                <MemoRightPanelAds 
                                                    items={visibleRightPanelItems} 
                                                    columns={4} 
                                                    showBrandChip={brandFilterOn} 
                                                    totalCount={pipedRightPanelItems.length}
                                                />
                                            </Box>
                                        )}
                                    </>
                                )}
                            </>
                        )}
                    </Box>
                </Box>
            </Paper>
            {/* Filters Drawer */}
            <AdsFiltersDrawer open={filtersDrawerOpen} onClose={() => setFiltersDrawerOpen(false)} />
        </Box>
    );
}
